import { useEffect, useMemo, useState } from 'react';
import { Plus, Upload, UserCheck, Search, Download, ClipboardList, Trash2, Pencil, X } from 'lucide-react';
import type { Team } from '@airmail/engine';
import type { GroupsConfig } from '@airmail/engine';
import { useCompetition, useSync, useTurnier } from '@/store/TournamentProvider';
import { Button, Card, Dialog, Empty, Field, Input, SectionHead, cx } from '@/ui';
import { confirmDialog } from '@/ui/dialogs';
import { ImportDialog } from '@/components/ImportDialog';
import { RegistrationsDialog } from '@/components/RegistrationsDialog';
import { Gate } from '@/components/ProMarke';
import { adminRegistrations } from '@/store/registration';
import { fullName, playerList } from '@/lib/format';
import { downloadText } from '@/lib/download';
import { locale, useT } from '@/i18n';
import { csvCell } from '@/lib/csv';
import { splitPersonName } from '@airmail/io';
import { isClubMode } from '@/lib/modes';

/** Für die Leitung: angemeldet, abgemeldet oder Warteliste (rückt automatisch nach); bestätigt zählt als angemeldet, DQ als abgemeldet */
const angemeldet = (team: Team) => team.status === 'registered' || team.status === 'confirmed';

export function TeamsPage() {
  const t = useT();
  const { dispatch, tournament, notify } = useTurnier();
  const sync = useSync();
  const comp = useCompetition();
  const [importOpen, setImportOpen] = useState(false);
  const [regOpen, setRegOpen] = useState(false);
  // Anmeldungen (docs/15): Zähler der noch nicht übernommenen, wenn Cloud-Sync an ist
  const [regCount, setRegCount] = useState<number | null>(null);
  const syncCfg = sync.cfg;
  useEffect(() => {
    if (!syncCfg) return setRegCount(null);
    let alive = true;
    // wie im Dialog: übernommen = gemerkt oder es gibt schon ein Team mit genau diesen Spielern
    const norm = (p: { firstName: string; lastName: string }) => `${p.firstName} ${p.lastName}`.trim().toLowerCase();
    const teamKeys = new Set(comp.teams.map((t) => t.players.map(norm).sort().join('|')));
    const known = new Set(syncCfg.importedRegs ?? []);
    adminRegistrations(syncCfg)
      .then((r) => alive && setRegCount(r.config ? r.registrations.filter((x) => !known.has(x.id) && !teamKeys.has(x.players.map(norm).sort().join('|'))).length : null))
      .catch(() => alive && setRegCount(null));
    return () => {
      alive = false;
    };
  }, [syncCfg, regOpen, comp.teams]);
  const [addOpen, setAddOpen] = useState(false);
  const [checkinMode, setCheckinMode] = useState(false);
  const [q, setQ] = useState('');
  const [editing, setEditing] = useState<Team | null>(null);

  const teams = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const list = comp.teams.filter(
      (t) => !needle || t.name.toLowerCase().includes(needle) || t.players.some((p) => `${p.firstName} ${p.lastName}`.toLowerCase().includes(needle)) || (t.club ?? '').toLowerCase().includes(needle),
    );
    return list.slice().sort((a, b) => (a.seed ?? 999) - (b.seed ?? 999) || a.name.localeCompare(b.name, 'de', { numeric: true }));
  }, [comp.teams, q]);

  const active = comp.teams.filter((t) => t.status === 'registered' || t.status === 'confirmed');
  const checkedIn = active.filter((t) => t.checkedIn).length;
  const unpaid = active.filter((t) => !t.paid).length;
  const waitlist = comp.teams.filter((t) => t.status === 'waitlist').length;
  const locked = comp.phases.some((p) => p.status !== 'planned');
  /**
   * Angemeldet / abgemeldet (Daniel, 10/2026: „ein Toggle, rot und grün —
   * bestätigt ist ja Check-in, Warteliste löst der Check-in, DQ wird einfach
   * abgemeldet"). Abmelden nach der Auslosung ist `team.withdraw` (offene Spiele
   * Walkover, Ergebnisse annulliert); wieder anmelden öffnet die Walkovers
   * wieder bzw. meldet nach — wie ein später Check-in. Alte Stände (bestätigt,
   * Warteliste, DQ) zählen als angemeldet bzw. abgemeldet.
   */
  const umschalten = async (team: Team) => {
    // Warteliste → von Hand nachrücken lassen (sonst rückt der Erste automatisch nach, wenn jemand abgemeldet wird)
    if (team.status === 'waitlist') {
      dispatch({ type: 'team.update', competitionId: comp.id, teamId: team.id, patch: { status: 'registered' } });
      return;
    }
    if (angemeldet(team)) {
      if (locked) {
        const partner = comp.phases.some((p) => p.type === 'groups' && (p.config as GroupsConfig).partners && p.status !== 'finished');
        const ok = await confirmDialog(
          partner
            ? t(
                '{team} abmelden? Die offenen Spiele mit {team} entfallen, gespielte Ergebnisse bleiben — ein Walkover träfe bei wechselnden Partnern den Partner. Wieder anmelden holt die Spiele zurück.',
                { team: team.name },
              )
            : t('{team} abmelden? Offene Spiele werden als Walkover gewertet, bisherige Ergebnisse annulliert. Wieder anmelden holt alles zurück.', { team: team.name }),
          { ok: t('Team abmelden'), danger: true },
        );
        if (ok) dispatch({ type: 'team.withdraw', competitionId: comp.id, teamId: team.id, mode: 'annul' });
      } else dispatch({ type: 'team.update', competitionId: comp.id, teamId: team.id, patch: { status: 'withdrawn' } });
      return;
    }
    dispatch({ type: 'team.update', competitionId: comp.id, teamId: team.id, patch: { status: 'registered' } });
  };
  // „Nur eingecheckte Teams auslosen" (docs/13 §4): nach der Auslosung haben nicht eingecheckte Teams noch keine Spiele
  const nurEingecheckte = comp.phases.some((p) => p.type === 'groups' && (p.config as GroupsConfig).checkedInOnly);
  const ausgelost = comp.phases.find((p) => p.type === 'groups' && p.status !== 'planned');
  const ohneSpiele = useMemo(
    () => new Set(ausgelost && nurEingecheckte ? active.filter((x) => !ausgelost.groups.some((g) => g.teamIds.includes(x.id))).map((x) => x.id) : []),
    [ausgelost, nurEingecheckte, active],
  );

  /*
   * Liga-Modus ohne Mannschaften (Daniel, 09/2026: „woher kommt das ‚Ohne
   * Mannschaft'?"). Wer seine Liste über den normalen Team-Import lädt, hat die
   * Mannschaft im Teamnamen stehen („Ceva 1") statt in einer eigenen Spalte —
   * `team.club` bleibt leer. Folge: Die Wertung sammelt alles unter „Ohne
   * Mannschaft", und die Auslosung streicht nichts weg, weil es nichts zu
   * streichen gibt: 16 Paare ergeben 120 Spiele statt 96.
   *
   * Das fällt erst in der Tabelle auf, also sagt es die Teams-Seite vorher —
   * mit einem Knopf, der die Mannschaft aus dem Namen übernimmt.
   */
  const clubMode = isClubMode(comp.phases.map((p) => p.config));
  /*
   * Ein Wort für ein Feld (Daniel, 09/2026: „denke das macht es klarer"). Die
   * Spalte hieß in der Tabelle „Verein", im Bearbeiten-Dialog und im
   * Import-Dialog aber schon „Verein / Ort" — drei Ansichten, zwei Wörter.
   * Im Liga-Modus heißt dasselbe Feld überall sonst „Mannschaft", also auch
   * hier: Ein drittes Wort für die Sache, an der gerade erst ein Missverständnis
   * hing, wäre das Letzte, was die Seite braucht.
   */
  const ortSpalte = clubMode ? t('Mannschaft') : t('Verein / Ort');
  const ohneGemeinde = comp.teams.filter((x) => !x.club?.trim());
  const ableitbar = ohneGemeinde.filter((x) => /\s*\d+\s*$/.test(x.name) && x.name.replace(/\s*\d+\s*$/, '').trim()).length;

  const update = (team: Team, patch: Partial<Omit<Team, 'id' | 'players'>>) => dispatch({ type: 'team.update', competitionId: comp.id, teamId: team.id, patch });
  /*
   * Zusatzfragen der Online-Anmeldung (docs/15 §6): jede Frage, die bei
   * irgendeinem Team beantwortet ist, wird eine Spalte — Kästchen zum
   * Umschalten wie „Bezahlt", Zahlen und Texte zum Lesen, unten die Summe.
   * Harald: „auf der Teilnehmerliste sollten dann neben ‚bezahlt' und
   * ‚Check-in' weitere Spalten existieren."
   */
  const antwortSpalten = useMemo(() => {
    const map = new Map<string, { id: string; label: string; bool: boolean }>();
    for (const team of comp.teams) for (const a of team.answers ?? []) if (!map.has(a.id)) map.set(a.id, { id: a.id, label: a.label, bool: typeof a.value === 'boolean' });
    return [...map.values()];
  }, [comp.teams]);
  const antwort = (team: Team, id: string) => team.answers?.find((a) => a.id === id)?.value;
  const antwortSetzen = (team: Team, spalte: { id: string; label: string }, value: string | number | boolean) => {
    const rest = (team.answers ?? []).filter((a) => a.id !== spalte.id);
    const next = value === false || value === '' ? rest : [...rest, { id: spalte.id, label: spalte.label, value }];
    update(team, { answers: next.length ? next : undefined });
  };
  const antwortSumme = (id: string): string => {
    const werte = active.map((x) => antwort(x, id)).filter((v) => v !== undefined && v !== false && v !== '');
    if (werte.length === 0) return '0';
    if (werte.every((v) => typeof v === 'boolean')) return String(werte.length);
    const zahlen = werte.map((v) => Number(v)).filter((n) => Number.isFinite(n));
    return zahlen.length === werte.length ? String(zahlen.reduce((s, n) => s + n, 0)) : String(werte.length);
  };
  // Alle Mannschaften/Vereine, die schon vorkommen — zum Auswählen statt Abtippen (Daniel: „am besten auf der Team-Seite Dropdown")
  const bekannteOrte = useMemo(() => [...new Set(comp.teams.map((x) => x.club?.trim()).filter((x): x is string => !!x))].sort((a, b) => a.localeCompare(b, 'de')), [comp.teams]);

  // Kasse (docs/notes/kasse.md): Startgeld je Team; 0/undefined = keins, dann nur die Zählung
  const fee = tournament.entryFee && tournament.entryFee > 0 ? tournament.entryFee : 0;
  const paidCount = active.length - unpaid;
  const money = (n: number) => n.toLocaleString(locale(), { style: 'currency', currency: 'EUR' });

  /*
   * Harald: „Bereits einige Male habe ich beim Import von Mannschaften Mist
   * gebaut, es wäre schön, alle auf einmal löschen zu können." Nur solange
   * nichts ausgelost ist — danach lehnt die Engine ab, dort hilft nur
   * Zurückziehen.
   *
   * `team.clearAll` ist bewusst **eine** Engine-Aktion und nicht zwanzig
   * einzelne: Sonst müsste man zwanzig Mal „Rückgängig" drücken, um einen
   * Fehlgriff zu heilen, und über den Sync ginge ein Schwall statt eines
   * Eintrags.
   */
  const alleLoeschen = async () => {
    if (comp.teams.length === 0) return;
    if (!(await confirmDialog(t('Wirklich alle {n} Teams löschen? „Rückgängig“ holt sie zurück.', { n: comp.teams.length }), { ok: t('Alle löschen'), danger: true }))) return;
    dispatch({ type: 'team.clearAll', competitionId: comp.id });
  };

  const markAllPaid = async () => {
    const open = active.filter((x) => !x.paid);
    if (open.length === 0) return;
    if (!(await confirmDialog(t('Alle {n} Teams als bezahlt markieren?', { n: open.length }), { ok: t('Als bezahlt markieren') }))) return;
    open.forEach((team, i) => dispatch({ type: 'team.update', competitionId: comp.id, teamId: team.id, patch: { paid: true } }, { silent: i < open.length - 1 }));
  };

  const exportKasseCsv = () => {
    const head = [t('Team'), t('Spieler'), ortSpalte, t('Startgeld'), t('Bezahlt'), 'Check-in', ...antwortSpalten.map((s) => s.label)];
    const lines = [head.join(';')];
    for (const team of comp.teams) {
      lines.push(
        [
          team.name,
          team.players.map(fullName).join(', '),
          team.club ?? '',
          fee ? money(fee) : '',
          team.paid ? t('ja') : t('nein'),
          team.checkedIn ? t('ja') : t('nein'),
          ...antwortSpalten.map((s) => antwortCsv(antwort(team, s.id))),
        ]
          .map(csvCell)
          .join(';'),
      );
    }
    downloadText(`airmail-kasse-${tournament.name.replace(/\s+/g, '-')}.csv`, '﻿' + lines.join('\r\n'), 'text/csv;charset=utf-8');
  };

  const exportCsv = () => {
    const head = [
      t('Team'),
      t('Kurzname'),
      t('Spieler {n}', { n: 1 }),
      t('Spieler {n}', { n: 2 }),
      t('Spieler {n}', { n: 3 }),
      t('Spieler {n}', { n: 4 }),
      ortSpalte,
      t('Setzung'),
      t('Bezahlt'),
      'Check-in',
      t('Status'),
      t('Notiz'),
      ...antwortSpalten.map((s) => s.label),
    ];
    const lines = [head.join(';')];
    for (const team of comp.teams) {
      const p = [0, 1, 2, 3].map((i) => {
        const pl = team.players[i];
        return pl ? fullName(pl) : '';
      });
      lines.push(
        [
          team.name,
          team.shortName,
          ...p,
          team.club ?? '',
          team.seed ?? '',
          team.paid ? t('ja') : t('nein'),
          team.checkedIn ? t('ja') : t('nein'),
          angemeldet(team) ? t('angemeldet') : team.status === 'waitlist' ? t('Warteliste') : t('abgemeldet'),
          team.note ?? '',
          ...antwortSpalten.map((s) => antwortCsv(antwort(team, s.id))),
        ]
          .map(csvCell)
          .join(';'),
      );
    }
    downloadText(`airmail-teams-${tournament.name.replace(/\s+/g, '-')}.csv`, '﻿' + lines.join('\r\n'), 'text/csv;charset=utf-8');
  };

  if (checkinMode) {
    return (
      <div>
        <SectionHead
          title="Check-in"
          meta={`${t('{a} von {b} eingecheckt · {n} offene Startgelder', { a: checkedIn, b: active.length, n: unpaid })}${fee ? ` · ${t('offen: {n} × {fee} = {sum}', { n: unpaid, fee: money(fee), sum: money(unpaid * fee) })}` : ''}`}
        >
          <Button onClick={() => setCheckinMode(false)}>{t('Fertig')}</Button>
        </SectionHead>
        <p className="text-sm text-muted mt-0 mb-4">
          {nurEingecheckte ? (
            <>
              {t('Ausgelost werden ')}
              <b>{t('nur eingecheckte Teams')}</b>
              {t('. Wer später kommt, tippt hier an und bekommt seine Spiele sofort — ohne die, die in der Saison schon gespielt sind.')}
            </>
          ) : (
            <>
              {t('Check-in ist nur die Anwesenheitsliste am Turniertag („ist da, hat bezahlt“). Ausgelost werden ')}
              <b>{t('alle gemeldeten Teams')}</b>
              {t(', auch ohne Check-in. Wer nicht kommt: Team unter „Teams“ auf ')}
              <b>{t('zurückgezogen')}</b>
              {t(' setzen — dann ist es aus der Auslosung raus.')}
            </>
          )}
        </p>
        <div className="grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-3">
          {active
            .slice()
            .sort((a, b) => a.name.localeCompare(b.name, 'de', { numeric: true }))
            .map((team) => (
              // Zwei Knöpfe nebeneinander statt ineinander: die Kachel (Check-in) und darüber, an
              // der Ecke, „Startgeld offen". Vorher steckte der zweite in einem role=button —
              // verschachtelte Bedienelemente, die Vorleseprogramme und Tastatur verwirren
              <div key={team.id} className="relative">
                <button
                  type="button"
                  aria-pressed={!!team.checkedIn}
                  onClick={() => update(team, { checkedIn: !team.checkedIn })}
                  className={cx(
                    'w-full text-left border px-4 py-4 cursor-pointer min-h-[92px] transition-colors select-none text-ink',
                    team.checkedIn ? 'bg-live/15 border-live' : 'bg-panel2 border-line hover:border-muted',
                  )}
                >
                  <div className="font-bold text-[16px] tracking-tight truncate">{team.name}</div>
                  <div className="text-xs text-dim truncate">{playerList(team)}</div>
                  <div className="mt-2 flex gap-2 items-center">
                    <span className={cx('label', team.checkedIn ? 'text-live' : 'text-dim')}>{team.checkedIn ? t('eingecheckt') : t('antippen')}</span>
                  </div>
                </button>
                {!team.paid && (
                  <button
                    type="button"
                    className="label absolute right-3 bottom-3 text-red border border-red px-1.5 py-0.5 cursor-pointer bg-panel2 max-md:min-h-9"
                    onClick={() => update(team, { paid: true })}
                    title={t('Als bezahlt markieren')}
                    aria-label={t('{offen}: Startgeld von {team} als bezahlt markieren', { offen: t('offen|Startgeld'), team: team.name })}
                  >
                    {t('offen|Startgeld')}
                  </button>
                )}
              </div>
            ))}
        </div>
      </div>
    );
  }

  return (
    <div>
      <SectionHead
        title={t('Teams')}
        meta={`${t(active.length === 1 ? '{n} Team gemeldet' : '{n} Teams gemeldet', { n: active.length })} · ${t('{n} eingecheckt', { n: checkedIn })}${waitlist ? ` · ${t('{n} Warteliste', { n: waitlist })}` : ''}${unpaid ? ` · ${t('{n} Startgeld offen', { n: unpaid })}` : ''}`}
      >
        <div className="relative">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-dim" />
          <Input className="pl-8 py-1.5 text-sm w-52" placeholder={t('Suchen')} value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <Button onClick={() => setCheckinMode(true)} data-tour="teams-checkin">
          <UserCheck size={14} /> {t('Check-in-Modus')}
        </Button>
        {/* Tabellen-Export (CSV für Excel) ist ein Pro-Merkmal (docs/21) — Import bleibt frei */}
        <Gate feature="excel">
          <Button onClick={exportCsv} title={t('Teamliste als CSV (Excel)')}>
            <Download size={14} /> CSV
          </Button>
        </Gate>
        {sync.cfg && (
          <Button onClick={() => setRegOpen(true)} title={t('Online-Anmeldungen vom Server übernehmen')}>
            <ClipboardList size={14} /> {t('Anmeldungen')}
            {regCount ? ` (${regCount})` : ''}
          </Button>
        )}
        <Button onClick={() => setImportOpen(true)} data-tour="teams-import">
          <Upload size={14} /> {t('Importieren')}
        </Button>
        {!locked && comp.teams.length > 0 && (
          <Button variant="ghost" onClick={alleLoeschen} title={t('Nach einem misslungenen Import: die ganze Liste leeren und neu importieren')}>
            <Trash2 size={14} /> {t('Alle löschen')}
          </Button>
        )}
        <Button variant="primary" onClick={() => setAddOpen(true)}>
          <Plus size={14} /> {t('Team')}
        </Button>
      </SectionHead>

      {locked && (
        <div className="text-xs text-warn mb-3">
          {t('Auslosung existiert schon — neue Teams werden nachgemeldet; statt löschen über den Status abmelden. Wer abgemeldet wird, macht Platz für den Ersten der Warteliste.')}
        </div>
      )}
      {ohneSpiele.size > 0 && (
        <div className="border border-warn text-warn bg-warn/5 px-3 py-2.5 mb-3 text-sm" data-ohne-spiele={ohneSpiele.size}>
          {t('{n} Teams sind noch nicht eingecheckt und haben deshalb keine Spiele. Häkchen bei Check-in (oder Check-in-Modus) legt ihre Spiele an — ohne die in der Saison schon gespielten.', {
            n: ohneSpiele.size,
          })}
        </div>
      )}

      {clubMode && !locked && ohneGemeinde.length > 0 && (
        <div className="border border-warn text-warn bg-warn/5 px-3 py-2.5 mb-3 flex items-center gap-3 flex-wrap text-sm">
          <span className="flex-1 min-w-[16rem]">
            {t('{n} Paaren fehlt die Mannschaft. Im Liga-Modus spielt dann jeder gegen jeden, statt nur gegen fremde Mannschaften.', { n: ohneGemeinde.length })}
          </span>
          {ableitbar > 0 && (
            <Button
              onClick={async () => {
                if (!(await confirmDialog(t('Bei {n} Paaren die Mannschaft aus dem Teamnamen übernehmen („Ceva 1“ → „Ceva“)?', { n: ableitbar }), { ok: t('Übernehmen') }))) return;
                dispatch({ type: 'team.clubsFromNames', competitionId: comp.id });
              }}
            >
              {t('Mannschaft aus dem Teamnamen')}
            </Button>
          )}
        </div>
      )}

      {comp.teams.length > 0 && (
        <Card title={t('Kasse')} className="mb-4">
          <div className="p-3 flex flex-wrap items-center gap-x-3 gap-y-2 text-sm">
            <span>{t('bezahlt {a} von {b} Teams', { a: paidCount, b: active.length })}</span>
            {fee > 0 && (
              <>
                <span className="text-dim">·</span>
                <span className="text-muted">{t('eingenommen {n} × {fee} = {sum}', { n: paidCount, fee: money(fee), sum: money(paidCount * fee) })}</span>
                <span className="text-dim">·</span>
                <span className={unpaid ? 'text-warn' : 'text-muted'}>{t('offen {n} × {fee} = {sum}', { n: unpaid, fee: money(fee), sum: money(unpaid * fee) })}</span>
              </>
            )}
            <div className="ml-auto flex flex-wrap gap-2">
              <Button onClick={markAllPaid} disabled={unpaid === 0}>
                {t('Alle als bezahlt markieren')}
              </Button>
              <Gate feature="excel">
                <Button onClick={exportKasseCsv} title={t('Kassenliste als CSV (Excel)')}>
                  <Download size={14} /> {t('Kassenliste (CSV)')}
                </Button>
              </Gate>
            </div>
          </div>
        </Card>
      )}

      {teams.length === 0 ? (
        comp.teams.length === 0 ? (
          <Empty title={t('Noch keine Teams')}>{t('Aus Excel kopieren und importieren — oder einzeln anlegen.')}</Empty>
        ) : (
          <Empty title={t('Kein Treffer.')} />
        )
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm min-w-[720px]">
            <thead>
              <tr className="border-b border-line">
                <Th>#</Th>
                <Th>{t('Team')}</Th>
                <Th>{t('Spieler')}</Th>
                <Th>{ortSpalte}</Th>
                <Th className="text-right w-20">{t('Setzung')}</Th>
                <Th className="text-center w-20">{t('Bezahlt')}</Th>
                <Th className="text-center w-20">Check-in</Th>
                {antwortSpalten.map((s) => (
                  <Th key={s.id} className={cx('w-24', s.bool ? 'text-center' : 'text-right')}>
                    <span title={t('Zusatzfrage aus der Anmeldung')}>{s.label}</span>
                  </Th>
                ))}
                <Th className="w-32">{t('Status')}</Th>
              </tr>
            </thead>
            <tbody>
              {teams.map((team, i) => (
                <tr key={team.id} className={cx('border-b border-line2 hover:bg-panel2', (team.status === 'withdrawn' || team.status === 'disqualified') && 'opacity-50')}>
                  <td className="px-2 py-2 font-mono text-[11px] text-dim">{i + 1}</td>
                  <td className="px-2 py-2">
                    {/* Harald (10/2026) fand den Dialog über den Namen nicht — deshalb zusätzlich ein Stift */}
                    <button className="font-bold tracking-tight cursor-pointer text-left hover:text-blue" onClick={() => setEditing(team)}>
                      {team.name}
                    </button>
                    <button
                      type="button"
                      className="text-dim hover:text-blue cursor-pointer ml-1.5 align-middle inline-flex p-1 -m-1"
                      onClick={() => setEditing(team)}
                      title={t('Namen und Spieler bearbeiten, löschen oder zurückziehen')}
                      aria-label={t('{team} bearbeiten', { team: team.name })}
                      data-team-bearbeiten
                    >
                      <Pencil size={12} />
                    </button>
                    {team.shortName !== team.name && <span className="text-dim text-xs ml-2">({team.shortName})</span>}
                    {ohneSpiele.has(team.id) && <span className="label text-warn ml-2">{t('nicht eingecheckt · keine Spiele')}</span>}
                  </td>
                  <td className="px-2 py-2 text-muted cursor-pointer hover:text-ink" onClick={() => setEditing(team)} title={t('Spielernamen ändern')}>
                    {team.players.map(fullName).join(', ') || <span className="text-warn">{t('Partner offen')}</span>}
                  </td>
                  <td className="px-2 py-1 text-muted">
                    {/* Auswahl aus den bekannten Orten; „anderer …" öffnet den Bearbeiten-Dialog für einen neuen Namen */}
                    <select
                      aria-label={ortSpalte}
                      className={cx('bg-transparent border border-transparent hover:border-line px-1 py-1 text-sm max-w-40 cursor-pointer', !team.club && 'text-dim')}
                      value={team.club && bekannteOrte.includes(team.club) ? team.club : team.club ? '__eigen' : ''}
                      onChange={(e) => {
                        if (e.target.value === '__neu') setEditing(team);
                        else if (e.target.value !== '__eigen') update(team, { club: e.target.value || undefined });
                      }}
                    >
                      <option value="">{t('– keine –')}</option>
                      {bekannteOrte.map((o) => (
                        <option key={o} value={o}>
                          {o}
                        </option>
                      ))}
                      {team.club && !bekannteOrte.includes(team.club) && <option value="__eigen">{team.club}</option>}
                      <option value="__neu">{t('anderer …')}</option>
                    </select>
                  </td>
                  <td className="px-2 py-2 text-right font-mono">{team.seed ?? ''}</td>
                  <td className="px-2 py-2 text-center">
                    <input type="checkbox" checked={team.paid} onChange={(e) => update(team, { paid: e.target.checked })} />
                  </td>
                  <td className="px-2 py-2 text-center">
                    <input type="checkbox" checked={team.checkedIn} onChange={(e) => update(team, { checkedIn: e.target.checked })} />
                  </td>
                  {antwortSpalten.map((s) => (
                    <td key={s.id} className={cx('px-2 py-2', s.bool ? 'text-center' : 'text-right font-mono text-[13px]')}>
                      {s.bool ? (
                        <input type="checkbox" aria-label={s.label} checked={antwort(team, s.id) === true} onChange={(e) => antwortSetzen(team, s, e.target.checked)} />
                      ) : (
                        <span>{antwort(team, s.id) ?? ''}</span>
                      )}
                    </td>
                  ))}
                  <td className="px-2 py-2">
                    <button
                      type="button"
                      aria-pressed={angemeldet(team)}
                      title={angemeldet(team) ? t('Team abmelden') : team.status === 'waitlist' ? t('Jetzt nachrücken lassen') : t('Wieder anmelden')}
                      onClick={() => void umschalten(team)}
                      className={cx(
                        'border px-2 py-0.5 text-[12px] font-bold cursor-pointer whitespace-nowrap max-md:min-h-11',
                        angemeldet(team) ? 'border-live text-live hover:bg-live/10' : team.status === 'waitlist' ? 'border-warn text-warn hover:bg-warn/10' : 'border-red text-red hover:bg-red/10',
                      )}
                      data-team-status={team.id}
                      data-angemeldet={angemeldet(team) ? '1' : team.status === 'waitlist' ? 'warteliste' : '0'}
                    >
                      {angemeldet(team) ? t('angemeldet') : team.status === 'waitlist' ? t('Warteliste') : t('abgemeldet')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
            {antwortSpalten.length > 0 && (
              <tfoot>
                <tr className="border-t border-line text-xs">
                  <td colSpan={7} className="px-2 py-2 text-dim text-right">
                    {t('Summe (ohne zurückgezogene)')}
                  </td>
                  {antwortSpalten.map((s) => (
                    <td key={s.id} className={cx('px-2 py-2 font-mono font-bold', s.bool ? 'text-center' : 'text-right')} data-summe={s.label}>
                      {antwortSumme(s.id)}
                    </td>
                  ))}
                  <td />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}

      <datalist id="airmail-orte">
        {bekannteOrte.map((o) => (
          <option key={o} value={o} />
        ))}
      </datalist>
      <RegistrationsDialog open={regOpen} onClose={() => setRegOpen(false)} />
      <ImportDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        teamSize={comp.ruleSet.teamSize}
        existingNames={comp.teams.map((t) => t.name)}
        onImport={(list) => {
          let ok = 0;
          for (const team of list) if (dispatch({ type: 'team.add', competitionId: comp.id, team }, { silent: true }).ok) ok++;
          dispatch({ type: 'tournament.update', patch: {} }, { silent: true });
          notify('ok', t('{ok} von {n} Teams importiert', { ok, n: list.length }));
        }}
      />
      <TeamDialog open={addOpen} onClose={() => setAddOpen(false)} teamSize={comp.ruleSet.teamSize} onSave={(input) => dispatch({ type: 'team.add', competitionId: comp.id, team: input })} />
      {editing && <EditTeamDialog team={editing} locked={locked} onClose={() => setEditing(null)} />}
    </div>
  );
}

/** Antwort als CSV-Zelle: Kästchen → ja/leer, sonst der Wert */
function antwortCsv(v: string | number | boolean | undefined): string {
  if (v === undefined || v === false || v === '') return '';
  return v === true ? 'ja' : String(v);
}

function Th({ children, className }: { children: React.ReactNode; className?: string }) {
  // Ausrichtung kommt aus className; sonst links — kein Kampf zweier text-*-Klassen
  return <th className={cx('px-2 py-2 label font-normal whitespace-nowrap', /text-(right|center)/.test(className ?? '') ? '' : 'text-left', className)}>{children}</th>;
}

function TeamDialog({
  open,
  onClose,
  teamSize,
  onSave,
}: {
  open: boolean;
  onClose: () => void;
  teamSize: number;
  onSave: (t: { name?: string; players: { firstName: string; lastName: string }[]; club?: string; seed?: number; paid?: boolean }) => void;
}) {
  const t = useT();
  const [name, setName] = useState('');
  const [players, setPlayers] = useState<string[]>(Array.from({ length: teamSize }, () => ''));
  const [club, setClub] = useState('');
  const [seed, setSeed] = useState('');
  const [paid, setPaid] = useState(false);
  const reset = () => {
    setName('');
    setPlayers(Array.from({ length: teamSize }, () => ''));
    setClub('');
    setSeed('');
    setPaid(false);
  };
  const save = (another: boolean) => {
    const ps = players.filter((p) => p.trim()).map(splitPersonName);
    if (!name.trim() && ps.length === 0) return;
    onSave({ name: name.trim() || undefined, players: ps, club: club.trim() || undefined, seed: seed ? Number(seed) : undefined, paid });
    reset();
    if (!another) onClose();
  };
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t('Team anlegen')}
      width="max-w-lg"
      footer={
        <>
          <span className="flex-1 text-xs text-dim">{t('Enter = speichern & nächstes')}</span>
          <Button variant="ghost" onClick={onClose}>
            {t('Schließen')}
          </Button>
          <Button onClick={() => save(true)}>{t('Speichern & nächstes')}</Button>
          <Button variant="primary" onClick={() => save(false)}>
            {t('Speichern')}
          </Button>
        </>
      }
    >
      <form
        className="grid gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          save(true);
        }}
      >
        <Field label={t('Teamname')} hint={t('Leer lassen → wird aus den Nachnamen gebildet')}>
          <Input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="Bag Brothers" />
        </Field>
        {players.map((p, i) => (
          <Field key={i} label={t('Spieler {n}', { n: i + 1 })}>
            <Input value={p} onChange={(e) => setPlayers(players.map((x, k) => (k === i ? e.target.value : x)))} placeholder={t('Vorname Nachname')} />
          </Field>
        ))}
        <div className="field-row grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label={t('Verein / Ort')}>
            <Input value={club} onChange={(e) => setClub(e.target.value)} list="airmail-orte" />
          </Field>
          <Field label={t('Setzung')}>
            <Input type="number" min={1} value={seed} onChange={(e) => setSeed(e.target.value)} />
          </Field>
          <Field label={t('Bezahlt')}>
            <label className="flex items-center gap-2 h-[41px]">
              <input type="checkbox" checked={paid} onChange={(e) => setPaid(e.target.checked)} /> {t('ja')}
            </label>
          </Field>
        </div>
        <button type="submit" hidden />
      </form>
    </Dialog>
  );
}

function EditTeamDialog({ team, locked, onClose }: { team: Team; locked: boolean; onClose: () => void }) {
  const t = useT();
  const { dispatch } = useTurnier();
  const comp = useCompetition();
  const [name, setName] = useState(team.name);
  const [shortName, setShortName] = useState(team.shortName);
  const [club, setClub] = useState(team.club ?? '');
  const [seed, setSeed] = useState(team.seed?.toString() ?? '');
  const [note, setNote] = useState(team.note ?? '');
  const [answers, setAnswers] = useState(team.answers ?? []);
  // Spielernamen (Harald, 10/2026: „Namen ändern können") — Anzahl nur vor der Auslosung änderbar
  const [spieler, setSpieler] = useState(() => team.players.map((p) => ({ firstName: p.firstName, lastName: p.lastName, substitute: !!p.substitute })));
  const spielerGeaendert =
    spieler.length !== team.players.length ||
    spieler.some((p, i) => p.firstName.trim() !== team.players[i]?.firstName || p.lastName.trim() !== team.players[i]?.lastName || p.substitute !== !!team.players[i]?.substitute);

  const save = () => {
    // Erst die Spieler: Stand der Teamname auf dem alten Standardnamen, zieht er dabei mit
    if (spielerGeaendert) {
      const r = dispatch({ type: 'team.setPlayers', competitionId: comp.id, teamId: team.id, players: spieler.filter((p) => p.firstName.trim() || p.lastName.trim()) });
      if (!r.ok) return;
    }
    const patch: Partial<Omit<Team, 'id' | 'players'>> = {
      // Unveränderter Name: nicht mitschicken, sonst überschreibt er den eben nachgezogenen Standardnamen
      ...(name.trim() && name.trim() !== team.name ? { name: name.trim() } : {}),
      // Unveränderten Kurznamen nicht mitschicken — sonst bliebe nach einer Namensänderung der alte stehen
      ...(shortName.trim() !== team.shortName ? { shortName: shortName.trim() || undefined } : {}),
      club: club.trim() || undefined,
      seed: seed ? Number(seed) : undefined,
      note: note.trim() || undefined,
    };
    if (team.answers?.length || answers.length)
      patch.answers = answers.filter((a) => a.value !== '' && a.value !== false).length ? answers.filter((a) => a.value !== '' && a.value !== false) : undefined;
    dispatch({ type: 'team.update', competitionId: comp.id, teamId: team.id, patch });
    onClose();
  };
  const withdraw = (mode: 'annul' | 'keep') => {
    dispatch({ type: 'team.withdraw', competitionId: comp.id, teamId: team.id, mode });
    onClose();
  };
  const remove = () => {
    if (dispatch({ type: 'team.remove', competitionId: comp.id, teamId: team.id }).ok) onClose();
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={team.name}
      width="max-w-lg"
      footer={
        <>
          {locked ? (
            angemeldet(team) && (
              <>
                <Button variant="danger" onClick={() => withdraw('annul')} title={t('Bisherige Ergebnisse annullieren, offene Spiele als Walkover')}>
                  {t('Team abmelden')}
                </Button>
                <Button variant="ghost" onClick={() => withdraw('keep')} title={t('Bisherige Ergebnisse behalten, offene als Walkover')}>
                  {t('… Ergebnisse behalten')}
                </Button>
              </>
            )
          ) : (
            <Button variant="danger" onClick={remove}>
              {t('Löschen')}
            </Button>
          )}
          <div className="flex-1" />
          <Button variant="ghost" onClick={onClose}>
            {t('Abbrechen')}
          </Button>
          <Button variant="primary" onClick={save}>
            {t('Speichern')}
          </Button>
        </>
      }
    >
      <div className="grid gap-3">
        <div className="field-row grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label={t('Teamname')}>
            <Input value={name} onChange={(e) => setName(e.target.value)} />
          </Field>
          <Field label={t('Kurzname (Beamer, Etikett)')}>
            <Input value={shortName} maxLength={18} onChange={(e) => setShortName(e.target.value)} />
          </Field>
        </div>
        <Field as="div" label={comp.ruleSet.teamSize === 1 ? t('Spieler') : t('Spieler (Vorname, Nachname)')}>
          <div className="grid gap-2" data-spieler-bearbeiten>
            {spieler.map((p, i) => (
              <div key={i} className="flex items-center gap-2">
                <Input
                  value={p.firstName}
                  placeholder={t('Vorname')}
                  aria-label={t('Vorname Spieler {n}', { n: i + 1 })}
                  onChange={(e) => setSpieler(spieler.map((x, k) => (k === i ? { ...x, firstName: e.target.value } : x)))}
                />
                <Input
                  value={p.lastName}
                  placeholder={t('Nachname')}
                  aria-label={t('Nachname Spieler {n}', { n: i + 1 })}
                  onChange={(e) => setSpieler(spieler.map((x, k) => (k === i ? { ...x, lastName: e.target.value } : x)))}
                />
                {!locked && spieler.length > 1 && (
                  <button
                    type="button"
                    className="text-dim hover:text-red cursor-pointer p-1"
                    onClick={() => setSpieler(spieler.filter((_, k) => k !== i))}
                    aria-label={t('Spieler {n} entfernen', { n: i + 1 })}
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
            ))}
            {!locked && (
              <button type="button" className="label text-left cursor-pointer hover:text-ink" onClick={() => setSpieler([...spieler, { firstName: '', lastName: '', substitute: false }])}>
                + {t('Spieler hinzufügen')}
              </button>
            )}
            {locked && <span className="text-xs text-dim">{t('Nach der Auslosung lassen sich nur die Namen ändern, nicht die Anzahl der Spieler.')}</span>}
          </div>
        </Field>
        <div className="field-row grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Field label={t('Verein / Ort')}>
            <Input value={club} onChange={(e) => setClub(e.target.value)} list="airmail-orte" />
          </Field>
          <Field label={t('Setzung')}>
            <Input type="number" min={1} value={seed} onChange={(e) => setSeed(e.target.value)} />
          </Field>
        </div>
        <Field label={t('Notiz')}>
          <Input value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>
        {answers.length > 0 && (
          <Field as="div" label={t('Antworten aus der Anmeldung')}>
            <div className="grid gap-2">
              {answers.map((a, i) => (
                <label key={a.id} className="flex items-center gap-3 text-sm">
                  <span className="w-40 shrink-0 text-muted truncate" title={a.label}>
                    {a.label}
                  </span>
                  {typeof a.value === 'boolean' ? (
                    <input type="checkbox" checked={a.value} onChange={(e) => setAnswers(answers.map((x, k) => (k === i ? { ...x, value: e.target.checked } : x)))} />
                  ) : (
                    <Input
                      value={String(a.value)}
                      onChange={(e) => setAnswers(answers.map((x, k) => (k === i ? { ...x, value: typeof a.value === 'number' ? Number(e.target.value) || 0 : e.target.value } : x)))}
                    />
                  )}
                </label>
              ))}
            </div>
          </Field>
        )}
      </div>
    </Dialog>
  );
}
